import React, { useState, useEffect } from 'react';
import { supabase } from '../integrations/supabase/client';
import { useToast } from '../hooks/use-toast';
import { Button } from './ui/button';
import { X, Upload, User, Mail, Phone, FileText, Building } from 'lucide-react';

interface CandidatoCadastroManualProps {
  isOpen: boolean;
  onClose: () => void;
  onCandidatoCriado: () => void;
}

interface Vaga {
  id: string;
  titulo: string;
  status: string;
}

export const CandidatoCadastroManual: React.FC<CandidatoCadastroManualProps> = ({
  isOpen,
  onClose,
  onCandidatoCriado
}) => {
  const [nome, setNome] = useState('');
  const [email, setEmail] = useState('');
  const [telefone, setTelefone] = useState('');
  const [vagaId, setVagaId] = useState('');
  const [origemLead, setOrigemLead] = useState('manual');
  const [observacoes, setObservacoes] = useState('');
  const [curriculo, setCurriculo] = useState<File | null>(null);
  const [vagas, setVagas] = useState<Vaga[]>([]);
  const [loading, setLoading] = useState(false);
  const [uploadingCurriculo, setUploadingCurriculo] = useState(false);
  const { toast } = useToast();

  useEffect(() => {
    if (isOpen) {
      carregarVagas();
    }
  }, [isOpen]);

  const carregarVagas = async () => {
    const { data, error } = await supabase
      .from('vagas_rh')
      .select('id, titulo, status')
      .eq('status', 'ativa')
      .order('titulo');

    if (error) {
      console.error('Erro ao carregar vagas:', error);
      return;
    }

    setVagas(data || []);
  };

  const handleCurriculoChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      // Validar tipo e tamanho do arquivo
      const allowedTypes = ['application/pdf', 'application/msword', 'application/vnd.openxmlformats-officedocument.wordprocessingml.document'];
      if (!allowedTypes.includes(file.type)) {
        toast({
          title: "Arquivo inválido",
          description: "Por favor, selecione um arquivo PDF ou Word",
          variant: "destructive"
        });
        return;
      }

      if (file.size > 10 * 1024 * 1024) { // 10MB
        toast({
          title: "Arquivo muito grande",
          description: "O arquivo deve ter no máximo 10MB",
          variant: "destructive"
        });
        return;
      }

      setCurriculo(file);
    }
  };

  const uploadCurriculo = async (candidatoId: string): Promise<string | null> => {
    if (!curriculo) return null;

    setUploadingCurriculo(true);
    
    try {
      const fileName = `${candidatoId}-${Date.now()}-${curriculo.name}`;
      
      const { error } = await supabase.storage
        .from('curriculos')
        .upload(fileName, curriculo);

      if (error) throw error;

      const { data: urlData } = supabase.storage
        .from('curriculos')
        .getPublicUrl(fileName);

      return urlData.publicUrl;
    } catch (error) {
      console.error('Erro ao fazer upload do currículo:', error);
      toast({
        title: "Erro no upload",
        description: "Não foi possível fazer upload do currículo",
        variant: "destructive"
      });
      return null;
    } finally {
      setUploadingCurriculo(false);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!nome || !email || !telefone) {
      toast({
        title: "Campos obrigatórios",
        description: "Por favor, preencha nome, email e telefone",
        variant: "destructive"
      });
      return;
    }

    setLoading(true);

    try {
      // Inserir candidato
      const { data: candidatoData, error: candidatoError } = await supabase
        .from('candidatos_rh')
        .insert({
          nome,
          email,
          telefone,
          vaga_id: vagaId || null,
          origem_lead: origemLead,
          observacoes: observacoes || null,
          tipo_cadastro: 'candidato',
          status: 'novo'
        })
        .select('id')
        .single();

      if (candidatoError) throw candidatoError;

      // Upload do currículo se fornecido
      let curriculoUrl = null;
      if (curriculo && candidatoData) {
        curriculoUrl = await uploadCurriculo(candidatoData.id);
        
        if (curriculoUrl) {
          // Atualizar candidato com URL do currículo
          const { error: updateError } = await supabase
            .from('candidatos_rh')
            .update({ curriculo_url: curriculoUrl })
            .eq('id', candidatoData.id);

          if (updateError) {
            console.error('Erro ao atualizar URL do currículo:', updateError);
          }
        }
      }

      // Adicionar entrada na timeline
      if (candidatoData) {
        await supabase
          .from('timeline_candidatos')
          .insert({
            candidato_id: candidatoData.id,
            titulo: 'Candidato cadastrado manualmente',
            descricao: `Candidato ${nome} foi cadastrado manualmente via sistema interno`,
            tipo: 'cadastro_manual',
            criado_por: 'Usuário do sistema'
          });
      }

      toast({
        title: "Candidato cadastrado",
        description: "Candidato foi cadastrado com sucesso!",
        variant: "default"
      });

      // Limpar formulário
      setNome('');
      setEmail('');
      setTelefone('');
      setVagaId('');
      setOrigemLead('manual');
      setObservacoes('');
      setCurriculo(null);

      onCandidatoCriado();
      onClose();

    } catch (error) {
      console.error('Erro ao cadastrar candidato:', error);
      toast({
        title: "Erro",
        description: "Erro ao cadastrar candidato. Tente novamente.",
        variant: "destructive"
      });
    } finally {
      setLoading(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-background rounded-lg shadow-lg max-w-2xl w-full max-h-[90vh] overflow-y-auto">
        <div className="flex items-center justify-between p-6 border-b border-border">
          <h2 className="text-xl font-semibold text-foreground flex items-center gap-2">
            <User className="h-5 w-5" />
            Cadastrar Candidato Manualmente
          </h2>
          <button
            onClick={onClose}
            className="text-muted-foreground hover:text-foreground transition-colors"
          >
            <X className="h-5 w-5" />
          </button>
        </div>

        <form onSubmit={handleSubmit} className="p-6 space-y-6">
          {/* Informações Básicas */}
          <div className="space-y-4">
            <h3 className="text-lg font-medium text-foreground flex items-center gap-2">
              <User className="h-4 w-4" />
              Informações Básicas
            </h3>
            
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-foreground mb-2">
                  Nome *
                </label>
                <input
                  type="text"
                  value={nome}
                  onChange={(e) => setNome(e.target.value)}
                  className="w-full px-3 py-2 border border-border rounded-md bg-background text-foreground"
                  placeholder="Nome completo do candidato"
                  required
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-foreground mb-2">
                  Email *
                </label>
                <div className="relative">
                  <Mail className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                  <input
                    type="email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    className="w-full pl-10 pr-3 py-2 border border-border rounded-md bg-background text-foreground"
                    placeholder="email@exemplo.com"
                    required
                  />
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-foreground mb-2">
                  Telefone *
                </label>
                <div className="relative">
                  <Phone className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                  <input
                    type="tel"
                    value={telefone}
                    onChange={(e) => setTelefone(e.target.value)}
                    className="w-full pl-10 pr-3 py-2 border border-border rounded-md bg-background text-foreground"
                    placeholder="(11) 99999-9999"
                    required
                  />
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-foreground mb-2">
                  Vaga de Interesse
                </label>
                <div className="relative">
                  <Building className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                  <select
                    value={vagaId}
                    onChange={(e) => setVagaId(e.target.value)}
                    className="w-full pl-10 pr-3 py-2 border border-border rounded-md bg-background text-foreground"
                  >
                    <option value="">Selecione uma vaga</option>
                    {vagas.map((vaga) => (
                      <option key={vaga.id} value={vaga.id}>
                        {vaga.titulo}
                      </option>
                    ))}
                  </select>
                </div>
              </div>
            </div>
          </div>

          {/* Origem e Currículo */}
          <div className="space-y-4">
            <h3 className="text-lg font-medium text-foreground flex items-center gap-2">
              <FileText className="h-4 w-4" />
              Documentos e Origem
            </h3>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-foreground mb-2">
                  Origem do Lead
                </label>
                <select
                  value={origemLead}
                  onChange={(e) => setOrigemLead(e.target.value)}
                  className="w-full px-3 py-2 border border-border rounded-md bg-background text-foreground"
                >
                  <option value="manual">Cadastro Manual</option>
                  <option value="linkedin">LinkedIn</option>
                  <option value="indeed">Indeed</option>
                  <option value="infojobs">InfoJobs</option>
                  <option value="catho">Catho</option>
                  <option value="vagas.com">Vagas.com</option>
                  <option value="indicacao">Indicação</option>
                  <option value="whatsapp">WhatsApp</option>
                  <option value="outros">Outros</option>
                </select>
              </div>

              <div>
                <label className="block text-sm font-medium text-foreground mb-2">
                  Currículo (PDF ou Word)
                </label>
                <div className="relative">
                  <Upload className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                  <input
                    type="file"
                    onChange={handleCurriculoChange}
                    accept=".pdf,.doc,.docx"
                    className="w-full pl-10 pr-3 py-2 border border-border rounded-md bg-background text-foreground file:mr-4 file:py-1 file:px-2 file:rounded file:border-0 file:text-sm file:bg-muted file:text-muted-foreground"
                  />
                </div>
                {curriculo && (
                  <p className="text-sm text-muted-foreground mt-1">
                    Arquivo selecionado: {curriculo.name}
                  </p>
                )}
              </div>
            </div>
          </div>

          {/* Observações */}
          <div>
            <label className="block text-sm font-medium text-foreground mb-2">
              Observações
            </label>
            <textarea
              value={observacoes}
              onChange={(e) => setObservacoes(e.target.value)}
              rows={4}
              className="w-full px-3 py-2 border border-border rounded-md bg-background text-foreground"
              placeholder="Observações adicionais sobre o candidato..."
            />
          </div>

          {/* Botões */}
          <div className="flex gap-3 pt-4">
            <Button
              type="button"
              variant="outline"
              onClick={onClose}
              className="flex-1"
            >
              Cancelar
            </Button>
            <Button
              type="submit"
              disabled={loading || uploadingCurriculo}
              className="flex-1"
            >
              {loading ? 'Cadastrando...' : uploadingCurriculo ? 'Fazendo upload...' : 'Cadastrar Candidato'}
            </Button>
          </div>
        </form>
      </div>
    </div>
  );
};